<template>
  <div class="flex flex-column justify-content-center h-screen" v-if="loading">
    <ProgressSpinner />
  </div>
  <Toast />
  <ConfirmPopup />
  <ConfirmDialog group="confirm-dialog" dismissableMask closable closeOnEscape />
  <DynamicDialog />
  <Error v-if="fatalError" :title="t('fatalError.title')" :message="t('fatalError.message')">
    <template #actions>
      <Button :label="t('fatalError.reload')" icon="pi pi-refresh" @click="reloadPage" />
      <Button
        :label="t('fatalError.goHome')"
        icon="pi pi-home"
        severity="secondary"
        @click="goHome"
      />
    </template>
  </Error>
  <RouterView v-else />
</template>

<script setup lang="ts">
import { watch, onErrorCaptured, ref } from 'vue'
import { storeToRefs } from 'pinia'
import DynamicDialog from 'primevue/dynamicdialog'
import ConfirmPopup from 'primevue/confirmpopup'
import ConfirmDialog from 'primevue/confirmdialog'
import Button from 'primevue/button'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { invoke, useTimeoutFn } from '@vueuse/core'

import { useLocaleStore } from '@/store'
import { useErrorHandler } from '@/composables/useErrorHandler'
import { isBenignError } from '@/utils/errorClassification'
import Error from '@/components/Error.vue'

const { lang } = storeToRefs(useLocaleStore())
const router = useRouter()
const { t } = useI18n()
const { fatalError, setFatalError } = useErrorHandler()

const loading = ref(false)
// Delay showing spinner, only when we are 'slow',
// e.g. when MS graph API is not cached.
const { start, stop } = useTimeoutFn(
  () => {
    loading.value = true
  },
  700,
  { immediate: false }
)

invoke(async () => {
  start()
  await router.isReady()
  stop()
  loading.value = false
})

watch(
  lang,
  (newLang) => {
    if (newLang) {
      document.documentElement.setAttribute('lang', newLang)
    }
  },
  { immediate: true }
)

function reloadPage() {
  window.location.reload()
}

function goHome() {
  // Full page load → no SPA navigation guards fire.
  window.location.assign('/')
}

onErrorCaptured((err) => {
  if (isBenignError(err)) {
    return false
  }
  setFatalError(err)
  console.error(err)
  return false
})
</script>

<i18n lang="yaml">
en:
  fatalError:
    title: 'Oops! Something went wrong'
    message: "The issue has been reported, and we're working to fix it as quickly as possible. Please try again later."
    reload: 'Reload'
    goHome: 'Go to home'
nl:
  fatalError:
    title: 'Oeps! Er is iets misgegaan'
    message: 'Het probleem is gemeld en we werken eraan om het zo snel mogelijk op te lossen. Probeer het later opnieuw.'
    reload: 'Herladen'
    goHome: 'Naar startpagina'
de:
  fatalError:
    title: 'Hoppla! Etwas ist schief gelaufen'
    message: 'Das Problem wurde gemeld und wir arbeiten daran, es so schnell wie möglich zu beheben. Bitte versuchen Sie es später erneut.'
    reload: 'Neu laden'
    goHome: 'Zur Startseite'
</i18n>
